import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiUser, FiPhone, FiMail, FiMapPin, FiX, FiShoppingBag, FiDollarSign, FiEye, FiPercent, FiChevronRight, FiAlertCircle, FiChevronUp, FiChevronDown, FiCheck, FiCreditCard, FiClock, FiGift, FiStar, FiShare2, FiPrinter } from 'react-icons/fi';
import { useShare } from './OrderManagement/hooks';
import { printOrder } from './OrderManagement/utils/printTemplate';
import { shareDebtQR } from './OrderManagement/utils/shareUtils';
import { generateOrderCode } from './OrderManagement/utils/formatters';
import { useSocket } from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
const ITEMS_PER_PAGE = 20;

const CustomerManagement = () => {
  const location = useLocation();
  const { socket } = useSocket();
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterDebt, setFilterDebt] = useState(location.state?.filterDebt || false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    points: ''
  });

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const searchDebounceRef = useRef(null);
  const isFirstRender = useRef(true);

  // Sort state: { field: 'totalSpent' | 'debt' | 'balance' | 'points' | null, direction: 'asc' | 'desc' }
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'desc' });

  // Pay debt modal
  const [showPayDebtModal, setShowPayDebtModal] = useState(false);
  const [payDebtCustomer, setPayDebtCustomer] = useState(null);
  const [payDebtAmount, setPayDebtAmount] = useState('');
  const [payDebtMethod, setPayDebtMethod] = useState('cash'); // cash or transfer
  const [payingDebt, setPayingDebt] = useState(false);
  const [debtOrders, setDebtOrders] = useState([]);
  const [selectedDebtOrders, setSelectedDebtOrders] = useState(new Set());
  const [loadingDebtOrders, setLoadingDebtOrders] = useState(false);
  const [sharingDebtQR, setSharingDebtQR] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Discount form for customer
  const [customerDiscount, setCustomerDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    percentage: '',
    categories: [],
    isActive: true
  });

  // Order history modal
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Shop settings for sharing
  const [shopSettings, setShopSettings] = useState(null);

  // User role for superadmin check
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  // Share hook
  const { handleShareBill, handleShareQR, isSharing } = useShare(shopSettings);

  // Bulk selection state (mobile)
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const longPressTimer = useRef(null);

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  // Sorted customers - now handled by server, just use customers directly
  // Keep sortedCustomers variable for backwards compatibility with existing JSX
  const sortedCustomers = customers;

  // Handle sort click - will trigger reload via useEffect
  const handleSort = (field) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        // Toggle direction or clear
        if (prev.direction === 'desc') {
          return { field, direction: 'asc' };
        } else {
          return { field: null, direction: 'desc' }; // Clear sort
        }
      }
      // New field, default desc
      return { field, direction: 'desc' };
    });
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <span className="ml-1 text-gray-300">↕</span>;
    }
    return sortConfig.direction === 'desc' 
      ? <FiChevronDown className="ml-1 inline" size={14} />
      : <FiChevronUp className="ml-1 inline" size={14} />
  };

  // Load customers with pagination
  const loadCustomers = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        skipCount: 'true'
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (filterDebt) params.hasDebt = 'true';
      
      // Add sort params
      if (sortConfig.field) {
        params.sortBy = sortConfig.field;
        params.sortOrder = sortConfig.direction;
      }

      const response = await axios.get(`${API_URL}/customers`, { params });
      const { data, pagination } = response.data;

      if (reset || pageNum === 1) {
        setCustomers(data || []);
      } else {
        setCustomers(prev => [...prev, ...(data || [])]);
      }

      setPage(pageNum);
      setHasMore(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải danh sách khách hàng');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, filterDebt, sortConfig]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadCustomers(page + 1, false);
  }, [hasMore, loadingMore, page, loadCustomers]);

  // Initial load
  useEffect(() => {
    loadCustomers(1, true);
    loadCategories();
    loadShopSettings();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for socket events to refresh customers (khi thanh toán nợ qua QR)
  useEffect(() => {
    if (socket) {
      const handleCustomerUpdated = async (data) => {
        console.log('Customer updated via socket:', data);
        // Refresh danh sách khách hàng
        loadCustomers(1, true);
        // Nếu đang mở modal thanh toán nợ, refresh debt orders
        if (showPayDebtModal && payDebtCustomer) {
          try {
            const response = await axios.get(`${API_URL}/customers/${payDebtCustomer._id}/debt-orders`);
            setDebtOrders(response.data.data || []);
            // Cập nhật lại thông tin customer
            const customerRes = await axios.get(`${API_URL}/customers/${payDebtCustomer._id}`);
            if (customerRes.data.data) {
              setPayDebtCustomer(customerRes.data.data);
            }
          } catch (error) {
            console.error('Error refreshing debt orders:', error);
          }
        }
        toast.success('💳 Đã nhận thanh toán nợ qua chuyển khoản');
      };
      
      const handlePaymentReceived = (data) => {
        // Chỉ refresh nếu message chứa "nợ"
        if (data.message && data.message.includes('nợ')) {
          loadCustomers(1, true);
        }
      };
      
      socket.on('customer-updated', handleCustomerUpdated);
      socket.on('payment-received', handlePaymentReceived);
      
      // Listen for bank account switch (from cron job)
      socket.on('bank-account-switched', (data) => {
        console.log('[Bank Switch] Received in CustomerManagement:', data);
        loadShopSettings(); // Reload shop settings to get new default bank
      });
      
      return () => {
        socket.off('customer-updated', handleCustomerUpdated);
        socket.off('payment-received', handlePaymentReceived);
        socket.off('bank-account-switched');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, showPayDebtModal, payDebtCustomer]);

  // Reload when search, filter, or sort changes (not on initial render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadCustomers(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterDebt, sortConfig]);

  // Reset page when filters or sort change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterDebt, sortConfig]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreItems]);

  // Pay debt function
  const openPayDebtModal = async (customer) => {
    setPayDebtCustomer(customer);
    setPayDebtAmount('');
    setPayDebtMethod('cash');
    setSelectedDebtOrders(new Set());
    setShowPayDebtModal(true);
    
    // Fetch debt orders
    setLoadingDebtOrders(true);
    try {
      const response = await axios.get(`${API_URL}/customers/${customer._id}/debt-orders`);
      setDebtOrders(response.data.data || []);
    } catch (error) {
      console.error('Error loading debt orders:', error);
      setDebtOrders([]);
    } finally {
      setLoadingDebtOrders(false);
    }
  };

  const toggleDebtOrder = (orderId) => {
    const newSelected = new Set(selectedDebtOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedDebtOrders(newSelected);
    
    // Calculate total from selected orders
    const selectedTotal = debtOrders
      .filter(o => newSelected.has(o._id))
      .reduce((sum, o) => sum + o.debtAmount, 0);
    setPayDebtAmount(selectedTotal > 0 ? selectedTotal.toString() : '');
  };

  const toggleAllDebtOrders = () => {
    if (selectedDebtOrders.size === debtOrders.length) {
      setSelectedDebtOrders(new Set());
      setPayDebtAmount('');
    } else {
      const allIds = new Set(debtOrders.map(o => o._id));
      setSelectedDebtOrders(allIds);
      const total = debtOrders.reduce((sum, o) => sum + o.debtAmount, 0);
      setPayDebtAmount(total.toString());
    }
  };

  const handlePayDebt = async () => {
    if (!payDebtCustomer || !payDebtAmount) return;
    
    const amount = parseFloat(payDebtAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Số tiền không hợp lệ');
      return;
    }
    
    setPayingDebt(true);
    try {
      await axios.post(`${API_URL}/customers/${payDebtCustomer._id}/pay-debt`, { 
        amount,
        paymentMethod: payDebtMethod,
        orderIds: Array.from(selectedDebtOrders),
        bankAccount: payDebtMethod === 'transfer' && selectedBankAccount ? {
          bankName: selectedBankAccount.bankName,
          accountNumber: selectedBankAccount.accountNumber,
          accountName: selectedBankAccount.accountName
        } : null
      });
      const methodLabel = payDebtMethod === 'transfer' ? 'chuyển khoản' : 'tiền mặt';
      toast.success(`Đã thanh toán ${formatCurrency(amount)} nợ (${methodLabel})`);
      setShowPayDebtModal(false);
      loadCustomers(1, true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể thanh toán nợ');
    } finally {
      setPayingDebt(false);
    }
  };

  const handleShareDebtQR = async () => {
    if (!payDebtCustomer || !shopSettings) return;
    
    const hasBank = shopSettings?.bankAccounts?.some(b => b.isDefault);
    if (!hasBank) {
      toast.error('Chưa cấu hình tài khoản ngân hàng mặc định');
      return;
    }
    
    if (payDebtCustomer.debt <= 0) {
      toast.error('Khách hàng không có nợ');
      return;
    }
    
    setSharingDebtQR(true);
    try {
      // Sử dụng customerId làm nội dung thanh toán
      const result = await shareDebtQR(
        payDebtCustomer._id,
        payDebtCustomer.name,
        payDebtCustomer.debt,
        shopSettings
      );
      
      if (result.downloaded) {
        toast.success('Đã tải xuống ảnh QR thanh toán nợ');
      } else if (!result.cancelled) {
        toast.success('Đã chia sẻ QR thanh toán nợ');
      }
    } catch (error) {
      console.error('Share debt QR error:', error);
      toast.error(error.message || 'Không thể chia sẻ QR thanh toán');
    } finally {
      setSharingDebtQR(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadShopSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      setShopSettings(response.data.data);
      
      // Set bank accounts
      if (response.data.data?.bankAccounts) {
        setBankAccounts(response.data.data.bankAccounts);
        const defaultBank = response.data.data.bankAccounts.find(b => b.isDefault);
        setSelectedBankAccount(defaultBank || response.data.data.bankAccounts[0] || null);
      }
    } catch (error) {
      console.error('Error loading shop settings:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(Math.round(amount || 0));
  };

  const openModal = async (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        notes: customer.notes || '',
        points: customer.points || 0
      });
      // Load customer discount
      try {
        const response = await axios.get(`${API_URL}/discounts/customer/${customer._id}`);
        const discount = response.data.data?.[0] || null;
        setCustomerDiscount(discount);
        if (discount) {
          setDiscountForm({
            percentage: discount.percentage,
            categories: discount.categories?.map(c => c._id) || [],
            isActive: discount.isActive
          });
        } else {
          setDiscountForm({ percentage: '', categories: [], isActive: true });
        }
      } catch (error) {
        setCustomerDiscount(null);
        setDiscountForm({ percentage: '', categories: [], isActive: true });
      }
    } else {
      setEditingCustomer(null);
      setFormData({ name: '', phone: '', email: '', address: '', notes: '', points: '' });
      setCustomerDiscount(null);
      setDiscountForm({ percentage: '', categories: [], isActive: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let customerId = editingCustomer?._id;
      
      if (editingCustomer) {
        await axios.put(`${API_URL}/customers/${editingCustomer._id}`, formData);
      } else {
        const response = await axios.post(`${API_URL}/customers`, formData);
        customerId = response.data.data._id;
      }

      // Save discount if percentage is set
      if (discountForm.percentage && parseFloat(discountForm.percentage) > 0) {
        const discountData = {
          name: `Ưu đãi cho ${formData.name}`,
          percentage: parseFloat(discountForm.percentage),
          categories: discountForm.categories,
          customer: customerId,
          isActive: discountForm.isActive
        };

        if (customerDiscount) {
          await axios.put(`${API_URL}/discounts/${customerDiscount._id}`, discountData);
        } else {
          await axios.post(`${API_URL}/discounts`, discountData);
        }
      } else if (customerDiscount) {
        // Remove discount if percentage is empty/0
        await axios.delete(`${API_URL}/discounts/${customerDiscount._id}`);
      }

      setShowModal(false);
      loadCustomers(1, true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể lưu khách hàng');
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setDiscountForm(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Xóa khách hàng này?')) return;
    try {
      await axios.delete(`${API_URL}/customers/${id}`);
      loadCustomers(1, true);
    } catch (error) {
      toast.error('Không thể xóa khách hàng');
    }
  };

  // Bulk selection functions
  const toggleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
        if (newSet.size === 0) setIsSelectionMode(false);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const selectAllCustomers = () => {
    setSelectedCustomers(new Set(sortedCustomers.map(c => c._id)));
  };

  const clearSelection = () => {
    setSelectedCustomers(new Set());
    setIsSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.size === 0) return;
    if (!window.confirm(`Xóa ${selectedCustomers.size} khách hàng đã chọn?`)) return;
    
    try {
      const deletePromises = Array.from(selectedCustomers).map(id => 
        axios.delete(`${API_URL}/customers/${id}`)
      );
      await Promise.all(deletePromises);
      clearSelection();
      loadCustomers(1, true);
    } catch (error) {
      toast.error('Không thể xóa một số khách hàng');
      loadCustomers(1, true);
    }
  };

  const handleLongPressStart = (customerId) => {
    longPressTimer.current = setTimeout(() => {
      setIsSelectionMode(true);
      setSelectedCustomers(new Set([customerId]));
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const viewOrderHistory = async (customer) => {
    setSelectedCustomer(customer);
    setShowOrderHistory(true);
    setLoadingOrders(true);
    try {
      // Always trigger debt-orders check to auto-fix legacy data
      await axios.get(`${API_URL}/customers/${customer._id}/debt-orders`);
      
      const response = await axios.get(`${API_URL}/orders/customer/${customer._id}`);
      setCustomerOrders(response.data.data || []);
    } catch (error) {
      toast.error('Không thể tải lịch sử đơn hàng');
      setCustomerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Check if debt is paid (API auto-fixes legacy data on load)
  const isDebtPaid = (order) => {
    if (!order?.debtAmount || order.debtAmount <= 0) return true;
    return !!order.debtPaidAt;
  };

  const getStatusLabel = (status, order) => {
    // Check if order has unpaid debt
    if (status === 'paid' && order?.debtAmount > 0 && !isDebtPaid(order)) {
      return 'Đang nợ';
    }
    const labels = {
      pending: 'Chờ xác nhận',
      preparing: 'Đang làm',
      completed: 'Hoàn thành',
      paid: 'Đã thanh toán',
      cancelled: 'Đã hủy'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status, order) => {
    // Check if order has unpaid debt
    if (status === 'paid' && order?.debtAmount > 0 && !isDebtPaid(order)) {
      return 'bg-orange-100 text-orange-800';
    }
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      paid: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodLabel = (method, bankAccount = null) => {
    const methods = {
      cash: 'Tiền mặt',
      transfer: 'Chuyển khoản',
      debt: 'Ghi nợ'
    };
    
    // If transfer and has bank account info, append it
    if (method === 'transfer' && bankAccount?.accountName && bankAccount?.accountNumber) {
      return `CK: ${bankAccount.accountName} - ${bankAccount.accountNumber}`;
    }
    
    return methods[method] || method;
  };

  // Mobile Customer Card - Simple click to view
  const MobileCustomerCard = ({ customer }) => {
    const isSelected = selectedCustomers.has(customer._id);
    
    const handleClick = () => {
      if (isSelectionMode) {
        toggleSelectCustomer(customer._id);
      } else {
        viewOrderHistory(customer);
      }
    };

    return (
      <div 
        className={`bg-white p-3 active:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
        onClick={handleClick}
        onTouchStart={() => handleLongPressStart(customer._id)}
        onTouchEnd={handleLongPressEnd}
        onTouchMove={handleLongPressEnd}
        onMouseDown={() => handleLongPressStart(customer._id)}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
      >
        <div className="flex items-center">
          {isSelectionMode && (
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-2 flex-shrink-0 ${
              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
            }`}>
              {isSelected && <FiCheck className="text-white" size={14} />}
            </div>
          )}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${customer.debt > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
            <FiUser className={customer.debt > 0 ? 'text-orange-600' : 'text-green-600'} size={18} />
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm text-gray-900 truncate">{customer.name}</p>
              <div className="flex gap-1">
                {customer.balance > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded font-medium">
                    TT: {new Intl.NumberFormat('vi-VN').format(Math.round(customer.balance / 1000))}K
                  </span>
                )}
                {customer.debt > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSelectionMode) openPayDebtModal(customer);
                    }}
                    className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded font-medium"
                  >
                    Nợ: {new Intl.NumberFormat('vi-VN').format(Math.round(customer.debt / 1000))}K
                  </button>
                )}
                {!customer.debt && !customer.balance && customer.discount && (
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                    -{customer.discount.percentage}%
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500">{customer.phone || 'Chưa có SĐT'}</p>
          </div>
          <div className="ml-2 text-right">
            <p className="text-xs text-gray-500">{customer.totalOrders || 0} đơn</p>
            <p className="text-sm font-semibold text-green-600">{formatCurrency(customer.totalSpent || 0)}</p>
            {customer.points > 0 && (
              <p className="text-xs text-amber-600">{customer.points} điểm</p>
            )}
          </div>
          {!isSelectionMode && <FiChevronRight className="ml-2 text-gray-300" size={18} />}
        </div>
      </div>
    );
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "p-6"}>
      {/* Header */}
      <div className={isMobile ? "bg-white px-4 py-3 flex items-center justify-between shadow-sm" : "flex justify-between items-center mb-6"}>
        <h1 className={isMobile ? "text-lg font-bold text-gray-800" : "text-2xl font-bold"}>Quản lý Khách hàng</h1>
        <button
          onClick={() => openModal()}
          className={isMobile 
            ? "w-9 h-9 bg-green-500 text-white rounded-full flex items-center justify-center"
            : "bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          }
        >
          <FiPlus size={isMobile ? 20 : 16} />
          {!isMobile && <span className="ml-2">Thêm khách hàng</span>}
        </button>
      </div>

      {/* Search */}
      <div className={isMobile ? "bg-white px-4 py-3" : "mb-6"}>
        <div className={isMobile ? "flex gap-2" : "flex gap-4 max-w-2xl"}>
          <div className={isMobile ? "relative flex-1" : "relative flex-1 max-w-md"}>
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={isMobile ? 16 : 20} />
            <input
              type="text"
              placeholder="Tìm theo tên hoặc SĐT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={isMobile ? "w-full pl-9 pr-3 py-2 text-sm border rounded-lg" : "w-full pl-10 pr-4 py-2 border rounded-lg"}
            />
          </div>
          <button
            onClick={() => setFilterDebt(!filterDebt)}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg border transition-colors ${
              filterDebt 
                ? 'bg-orange-100 border-orange-300 text-orange-700' 
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FiAlertCircle size={isMobile ? 16 : 18} />
            {!isMobile && <span>Đang nợ</span>}
          </button>
        </div>
        {filterDebt && (
          <div className="mt-2 text-sm text-orange-600">
            Đang lọc khách hàng có nợ
          </div>
        )}
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-8 text-sm">Đang tải...</div>
      ) : customers.length === 0 ? (
        <div className={isMobile ? "mx-4 bg-white rounded-lg p-8 text-center text-gray-500 text-sm" : "bg-white rounded-lg shadow p-8 text-center text-gray-500"}>
          {search ? 'Không tìm thấy khách hàng' : 'Chưa có khách hàng nào'}
        </div>
      ) : isMobile ? (
        // Mobile: Card list with sort options
        <div>
          {/* Mobile Sort Buttons */}
          <div className="px-4 py-2 flex gap-2 bg-white border-b overflow-x-auto">
            <span className="text-xs text-gray-500 self-center flex-shrink-0">Sắp xếp:</span>
            <button
              onClick={() => handleSort('totalSpent')}
              className={`px-2 py-1 text-xs rounded-full border whitespace-nowrap ${
                sortConfig.field === 'totalSpent' 
                  ? 'bg-green-100 border-green-300 text-green-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-600'
              }`}
            >
              Chi tiêu {sortConfig.field === 'totalSpent' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
            </button>
            <button
              onClick={() => handleSort('points')}
              className={`px-2 py-1 text-xs rounded-full border whitespace-nowrap ${
                sortConfig.field === 'points' 
                  ? 'bg-amber-100 border-amber-300 text-amber-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-600'
              }`}
            >
              Điểm {sortConfig.field === 'points' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
            </button>
            <button
              onClick={() => handleSort('debt')}
              className={`px-2 py-1 text-xs rounded-full border whitespace-nowrap ${
                sortConfig.field === 'debt' 
                  ? 'bg-orange-100 border-orange-300 text-orange-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-600'
              }`}
            >
              Nợ {sortConfig.field === 'debt' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
            </button>
          </div>
          
          {/* Bulk Selection Bar */}
          {isSelectionMode && (
            <div className="px-4 py-2 bg-blue-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700 font-medium">
                  Đã chọn {selectedCustomers.size}
                </span>
                <button
                  onClick={selectAllCustomers}
                  className="text-xs text-blue-600 underline"
                >
                  Chọn tất cả
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-lg"
                >
                  Hủy
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg flex items-center gap-1"
                >
                  <FiTrash2 size={12} />
                  Xóa
                </button>
              </div>
            </div>
          )}
          
          {/* Hint for long press */}
          {!isSelectionMode && sortedCustomers.length > 0 && (
            <div className="px-4 py-1 text-xs text-gray-400 text-center bg-gray-50">
              Giữ để chọn nhiều khách hàng
            </div>
          )}
          
          <div className="divide-y divide-gray-100">
            {sortedCustomers.map(customer => (
              <MobileCustomerCard key={customer._id} customer={customer} />
            ))}
          </div>
          
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
          )}
          
          {/* Load more trigger */}
          {!loadingMore && hasMore && (
            <div ref={observerTarget} className="h-10" />
          )}
        </div>
      ) : (
        // Desktop: Table
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Khách hàng</th>
                <th className="px-4 py-3 text-left font-semibold">Liên hệ</th>
                <th className="px-4 py-3 text-center font-semibold">Đơn hàng</th>
                <th 
                  className="px-4 py-3 text-right font-semibold cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('totalSpent')}
                >
                  <span className="inline-flex items-center">
                    Tổng chi tiêu
                    <SortIcon field="totalSpent" />
                  </span>
                </th>
                <th 
                  className="px-4 py-3 text-right font-semibold cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('points')}
                >
                  <span className="inline-flex items-center">
                    Điểm
                    <SortIcon field="points" />
                  </span>
                </th>
                <th 
                  className="px-4 py-3 text-right font-semibold cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('debt')}
                >
                  <span className="inline-flex items-center">
                    Nợ
                    <SortIcon field="debt" />
                  </span>
                </th>
                <th 
                  className="px-4 py-3 text-right font-semibold cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('balance')}
                >
                  <span className="inline-flex items-center">
                    Tiền thừa
                    <SortIcon field="balance" />
                  </span>
                </th>
                <th className="px-4 py-3 text-center font-semibold">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedCustomers.map(customer => (
                <tr key={customer._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${customer.debt > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
                        <FiUser className={customer.debt > 0 ? 'text-orange-600' : 'text-green-600'} />
                      </div>
                      <div>
                        <div className="font-semibold">{customer.name}</div>
                        {customer.notes && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">{customer.notes}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {customer.phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <FiPhone className="mr-1" size={14} /> {customer.phone}
                      </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <FiMail className="mr-1" size={14} /> {customer.email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                      <FiShoppingBag className="inline mr-1" size={14} />
                      {customer.totalOrders || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">
                    {formatCurrency(customer.totalSpent || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {customer.points > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                        <FiGift size={14} />
                        {new Intl.NumberFormat('vi-VN').format(customer.points)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {customer.debt > 0 ? (
                      <button
                        onClick={() => openPayDebtModal(customer)}
                        className="text-orange-600 font-semibold hover:underline"
                        title="Click để thanh toán nợ"
                      >
                        {formatCurrency(customer.debt)}
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {customer.balance > 0 ? (
                      <span className="text-blue-600 font-semibold">
                        {formatCurrency(customer.balance)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => viewOrderHistory(customer)}
                        className="text-green-600 hover:bg-green-100 p-2 rounded"
                        title="Xem lịch sử đơn hàng"
                      >
                        <FiEye />
                      </button>
                      <button
                        onClick={() => openModal(customer)}
                        className="text-blue-600 hover:bg-blue-100 p-2 rounded"
                        title="Sửa thông tin"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDelete(customer._id)}
                        className="text-red-600 hover:bg-red-100 p-2 rounded"
                        title="Xóa khách hàng"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
          )}
          
          {/* Load more trigger */}
          {!loadingMore && hasMore && (
            <div ref={observerTarget} className="h-10" />
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[80vh] md:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingCustomer ? 'Sửa khách hàng' : 'Thêm khách hàng'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">
                  <FiUser className="inline mr-2" />Tên khách hàng *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  <FiPhone className="inline mr-2" />Số điện thoại
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  <FiMail className="inline mr-2" />Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  <FiMapPin className="inline mr-2" />Địa chỉ
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  rows="2"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">Ghi chú</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  rows="2"
                  placeholder="VD: Khách quen, thích đồ ngọt..."
                />
              </div>

              {/* Points Edit - Only show when editing */}
              {editingCustomer && (
                <div>
                  <label className="block font-semibold mb-2">
                    <FiGift className="inline mr-2 text-amber-500" />Điểm thưởng
                  </label>
                  <input
                    type="number"
                    value={formData.points}
                    onChange={(e) => setFormData({ ...formData, points: e.target.value })}
                    className="w-full border rounded-lg px-4 py-2"
                    min="0"
                    placeholder="Số điểm thưởng"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Điểm hiện tại: {new Intl.NumberFormat('vi-VN').format(editingCustomer.points || 0)}
                  </p>
                </div>
              )}

              {/* Customer Discount Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3 flex items-center text-green-700">
                  <FiPercent className="mr-2" /> Khuyến mãi riêng cho khách này
                </h3>
                
                <div className="space-y-3 bg-green-50 p-3 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-1">Phần trăm giảm giá</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={discountForm.percentage}
                        onChange={(e) => setDiscountForm({ ...discountForm, percentage: e.target.value })}
                        className="w-full border rounded-lg px-4 py-2 pr-10"
                        placeholder="VD: 10"
                        min="0"
                        max="100"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Để trống nếu không có khuyến mãi</p>
                  </div>

                  {discountForm.percentage && parseFloat(discountForm.percentage) > 0 && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Áp dụng cho danh mục</label>
                        <p className="text-xs text-gray-500 mb-2">Bỏ trống = áp dụng tất cả</p>
                        <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border rounded-lg p-2 bg-white">
                          {categories.map(category => (
                            <label key={category._id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded text-sm">
                              <input
                                type="checkbox"
                                checked={discountForm.categories.includes(category._id)}
                                onChange={() => handleCategoryToggle(category._id)}
                                className="w-4 h-4 text-green-600 rounded"
                              />
                              <span>{category.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="discountActive"
                          checked={discountForm.isActive}
                          onChange={(e) => setDiscountForm({ ...discountForm, isActive: e.target.checked })}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                        <label htmlFor="discountActive" className="text-sm">Kích hoạt khuyến mãi</label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  {editingCustomer ? 'Cập nhật' : 'Thêm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order History Modal */}
      {showOrderHistory && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-2 md:p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] md:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-3 md:p-4 border-b bg-green-50">
              <div>
                <h2 className="text-lg md:text-xl font-bold">Lịch sử đơn hàng</h2>
                <p className="text-sm text-gray-600">{selectedCustomer.name}</p>
              </div>
              <button onClick={() => setShowOrderHistory(false)}>
                <FiX size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 md:p-4">
              {loadingOrders ? (
                <div className="text-center py-8">Đang tải...</div>
              ) : customerOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Chưa có đơn hàng nào
                </div>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {customerOrders.map(order => (
                    <div key={order._id} className={`border rounded-lg p-3 md:p-4 ${
                      order.debtAmount > 0 && !isDebtPaid(order) ? 'border-orange-200 bg-orange-50/30' : ''
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 flex-wrap gap-1">
                            <span className="font-semibold">
                              {order.table?.name || `Bàn ${order.tableNumber || 'Mang đi'}`}
                            </span>
                            <span className="text-xs text-gray-500 font-mono">
                              #{generateOrderCode(order)}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(order.status, order)}`}>
                              {getStatusLabel(order.status, order)}
                            </span>
                          </div>
                          {/* Payment method on separate row for transfer with bank info */}
                          {order.status === 'paid' && order.paymentMethod && (
                            <div className="mt-1 overflow-x-auto">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                                order.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' :
                                order.paymentMethod === 'debt' ? 'bg-orange-100 text-orange-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {getPaymentMethodLabel(order.paymentMethod, order.bankAccount)}
                              </span>
                            </div>
                          )}
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <FiClock className="mr-1" size={14} />
                            {new Date(order.createdAt).toLocaleString('vi-VN')}
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="font-bold text-green-600">
                            {formatCurrency(order.totalAmount)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.items.reduce((sum, i) => sum + i.quantity, 0)} món
                          </div>
                        </div>
                      </div>
                      
                      {/* Debt info - hiển thị khi đang nợ hoặc đã trả nợ */}
                      {(order.debtAmount > 0 || order.debtPaidAt) && (
                        <div className={`mb-2 p-2 rounded-lg text-sm ${
                          order.debtPaidAt ? 'bg-green-50' : 'bg-orange-50'
                        }`}>
                          {order.debtPaidAt ? (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-green-700">
                                  <FiCheck className="inline mr-1" size={14} />
                                  Đã trả nợ {formatCurrency(order.totalAmount)}
                                </span>
                                <span className="text-green-600 text-xs">
                                  {new Date(order.debtPaidAt).toLocaleString('vi-VN')}
                                </span>
                              </div>
                              {order.debtPaidMethod === 'transfer' && order.debtPaidBankAccount?.accountName && (
                                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block">
                                  CK: {order.debtPaidBankAccount.bankName} - {order.debtPaidBankAccount.accountName}
                                </div>
                              )}
                              {order.debtPaidMethod === 'transfer' && order.debtPaidBankAccount?.accountNumber && !order.debtPaidBankAccount?.accountName && (
                                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block">
                                  CK: {order.debtPaidBankAccount.bankName || 'Ngân hàng'} - {order.debtPaidBankAccount.accountNumber}
                                </div>
                              )}
                              {order.debtPaidMethod === 'cash' && (
                                <div className="text-xs text-green-600">Tiền mặt</div>
                              )}
                              {order.debtPaidMethod === 'transfer' && !order.debtPaidBankAccount?.accountNumber && (
                                <div className="text-xs text-blue-600">Chuyển khoản</div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between text-orange-700">
                              <span>Đang nợ: {formatCurrency(order.debtAmount)}</span>
                              <span className="text-xs">/ Tổng {formatCurrency(order.totalAmount)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="border-t pt-2">
                        <div className="text-sm text-gray-600">
                          {order.items.map((item, idx) => (
                            <span key={idx}>
                              {item.name} x{item.quantity}
                              {idx < order.items.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                        
                        {/* Action buttons */}
                        {order.status === 'paid' && (
                          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-dashed">
                            <button
                              onClick={() => handleShareBill(order)}
                              disabled={isSharing(order._id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                              {isSharing(order._id, 'bill') ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <FiShare2 size={14} />
                              )}
                              <span>Share</span>
                            </button>
                            <button
                              onClick={() => printOrder(order, shopSettings)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                            >
                              <FiPrinter size={14} />
                              <span>In</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t p-4 bg-gray-50">
              <div className="flex justify-between text-sm">
                <span>Tổng đơn hàng:</span>
                <span className="font-semibold">{selectedCustomer.totalOrders || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Tổng chi tiêu:</span>
                <span className="font-semibold text-green-600">{formatCurrency(selectedCustomer.totalSpent || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="flex items-center gap-1">
                  <FiGift size={14} className="text-amber-500" />
                  Điểm thưởng:
                </span>
                <span className="font-semibold text-amber-600">{new Intl.NumberFormat('vi-VN').format(selectedCustomer.points || 0)}</span>
              </div>
              {selectedCustomer.debt > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span>Đang nợ:</span>
                  <span className="font-semibold text-orange-600">{formatCurrency(selectedCustomer.debt)}</span>
                </div>
              )}
              {selectedCustomer.balance > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span>Tiền thừa:</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(selectedCustomer.balance)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pay Debt Modal */}
      {showPayDebtModal && payDebtCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-4 md:p-6 max-h-[80vh] md:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="text-lg md:text-xl font-bold">Thanh toán nợ</h2>
              <button onClick={() => setShowPayDebtModal(false)}>
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-3 md:mb-4 p-2 md:p-3 bg-orange-50 rounded-lg text-sm md:text-base">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Khách hàng:</span>
                <span className="font-bold">{payDebtCustomer.name}</span>
              </div>
              {payDebtCustomer.phone && (
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">SĐT:</span>
                  <span className="text-gray-700">{payDebtCustomer.phone}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <div className="flex justify-between flex-1">
                  <span className="text-gray-600">Tổng nợ:</span>
                  <span className="font-bold text-orange-600">{formatCurrency(payDebtCustomer.debt)}</span>
                </div>
                <button
                  onClick={handleShareDebtQR}
                  disabled={sharingDebtQR || payDebtCustomer.debt <= 0}
                  className="ml-3 p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Share QR thanh toán nợ"
                >
                  {sharingDebtQR ? (
                    <span className="animate-spin inline-block">⏳</span>
                  ) : (
                    <FiShare2 size={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Debt Orders List */}
            <div className="mb-3 md:mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Các đơn hàng đang nợ</label>
                {debtOrders.length > 0 && (
                  <button
                    onClick={toggleAllDebtOrders}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedDebtOrders.size === debtOrders.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                  </button>
                )}
              </div>
              
              {loadingDebtOrders ? (
                <div className="text-center py-3 text-gray-500 text-sm">Đang tải...</div>
              ) : debtOrders.length === 0 ? (
                <div className="text-center py-3 text-gray-500 bg-gray-50 rounded-lg text-sm">
                  Không có đơn hàng nợ
                </div>
              ) : (
                <div className="max-h-28 md:max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {debtOrders.map(order => (
                    <label 
                      key={order._id} 
                      className={`flex items-center p-2 cursor-pointer hover:bg-gray-50 ${
                        selectedDebtOrders.has(order._id) ? 'bg-orange-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDebtOrders.has(order._id)}
                        onChange={() => toggleDebtOrder(order._id)}
                        className="w-4 h-4 text-orange-600 rounded mr-2"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <span className="font-medium text-sm">
                            {order.table?.name || `Bàn ${order.tableNumber || 'Mang đi'}`}
                          </span>
                          <span className="text-orange-600 font-semibold text-sm">
                            {formatCurrency(order.debtAmount)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString('vi-VN')} - Tổng: {formatCurrency(order.totalAmount)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {selectedDebtOrders.size > 0 && (
                <div className="mt-1 text-sm text-orange-600 font-medium">
                  Đã chọn {selectedDebtOrders.size} đơn = {formatCurrency(
                    debtOrders.filter(o => selectedDebtOrders.has(o._id)).reduce((sum, o) => sum + o.debtAmount, 0)
                  )}
                </div>
              )}
            </div>

            {/* Payment Method Selection */}
            <div className="mb-3 md:mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">Hình thức thanh toán</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setPayDebtMethod('cash')}
                  className={`p-2 border-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                    payDebtMethod === 'cash' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FiDollarSign size={18} className={payDebtMethod === 'cash' ? 'text-green-600' : 'text-gray-400'} />
                  <span className={`text-sm font-medium ${payDebtMethod === 'cash' ? 'text-green-600' : 'text-gray-600'}`}>
                    Tiền mặt
                  </span>
                </button>
                <button
                  onClick={() => setPayDebtMethod('transfer')}
                  className={`p-2 border-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                    payDebtMethod === 'transfer' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FiCreditCard size={18} className={payDebtMethod === 'transfer' ? 'text-blue-600' : 'text-gray-400'} />
                  <span className={`text-sm font-medium ${payDebtMethod === 'transfer' ? 'text-blue-600' : 'text-gray-600'}`}>
                    Chuyển khoản
                  </span>
                </button>
              </div>
            </div>

            {/* Bank Account Selection - Only for superadmin when transfer */}
            {payDebtMethod === 'transfer' && isSuperAdmin && bankAccounts.length > 0 && (
              <div className="mb-3 md:mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chọn tài khoản ngân hàng</label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {bankAccounts.map((bank, index) => (
                    <label
                      key={index}
                      className={`flex items-center p-2 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedBankAccount?.accountNumber === bank.accountNumber
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="bankAccount"
                        checked={selectedBankAccount?.accountNumber === bank.accountNumber}
                        onChange={() => setSelectedBankAccount(bank)}
                        className="w-4 h-4 text-blue-600 mr-2"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{bank.bankName}</div>
                        <div className="text-xs text-gray-500 truncate">{bank.accountNumber} - {bank.accountName}</div>
                      </div>
                      {bank.isDefault && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Mặc định</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="mb-3 md:mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền thanh toán</label>
              <input
                type="number"
                value={payDebtAmount}
                onChange={(e) => setPayDebtAmount(e.target.value)}
                placeholder="Chọn đơn hàng hoặc nhập số tiền"
                max={payDebtCustomer.debt}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
              />
              
              {/* Quick buttons */}
              <div className="flex gap-2 mt-1.5">
                <button
                  onClick={() => {
                    toggleAllDebtOrders();
                    if (selectedDebtOrders.size !== debtOrders.length) {
                      setPayDebtAmount(payDebtCustomer.debt.toString());
                    }
                  }}
                  className="px-2 py-1 text-xs md:text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium"
                >
                  Trả hết ({formatCurrency(payDebtCustomer.debt)})
                </button>
                <button
                  onClick={() => {
                    setSelectedDebtOrders(new Set());
                    setPayDebtAmount('');
                  }}
                  className="px-2 py-1 text-xs md:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Xóa
                </button>
              </div>
            </div>

            {/* Remaining debt info */}
            {parseFloat(payDebtAmount) > 0 && parseFloat(payDebtAmount) < payDebtCustomer.debt && (
              <div className="mb-3 p-2 bg-yellow-50 rounded-lg text-xs md:text-sm text-yellow-700 flex justify-between">
                <span>Còn nợ sau khi thanh toán:</span>
                <span className="font-medium">{formatCurrency(payDebtCustomer.debt - parseFloat(payDebtAmount))}</span>
              </div>
            )}

            {parseFloat(payDebtAmount) >= payDebtCustomer.debt && (
              <div className="mb-3 p-2 bg-green-50 rounded-lg text-xs md:text-sm text-green-700 flex items-center justify-center">
                <FiCheck className="mr-1" /> Thanh toán hết nợ
              </div>
            )}

            <div className="flex space-x-2 md:space-x-3">
              <button
                onClick={() => setShowPayDebtModal(false)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-100 text-sm"
              >
                Hủy
              </button>
              <button
                onClick={handlePayDebt}
                disabled={payingDebt || !payDebtAmount || parseFloat(payDebtAmount) <= 0}
                className={`flex-1 py-2 text-white rounded-lg font-bold disabled:opacity-50 text-sm ${
                  payDebtMethod === 'transfer' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {payingDebt ? 'Đang xử lý...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
